import { MenuItem, Select } from '@material-ui/core'
import { graphql, useStaticQuery } from 'gatsby'
import GoogleMapReact from 'google-map-react'
import React, { useEffect, useState, useMemo } from 'react'
import { API_GOOGLE_KEY } from '../../../state/constants/appConstants'
import ListPoint from '../list-point/list-point'
import Point from '../point/point'
import useLocaleMode from '../../../hooks/useLocaleMode'
import Axios from 'axios'
import './map.scss'

const styleMap = {
	display: 'block',
	float: 'left',
	height: '100%',
	position: 'relative',
	width: '70%',
	margin: '0 auto',
}

const Map = () => {
	const { locale } = useLocaleMode()

	const query = useStaticQuery(graphql`
		query COUNTRIES_MAPS {
			allContentfulTenaPaises(sort: { orden: ASC }, filter: { node_locale: { eq: "es-CO" } }) {
				nodes {
					codigoDeDosLetras
					lenguaje
					titulo
					dominio
					nombreDepartamento
					nombreCiudad
					departamentos {
						codigo
						nombre
					}
					ciudades {
						codigo
						codigoDepartamento
						nombre
					}
					puntosDeVenta {
						lat
						lng
						name
						description
						codigoCiudad
						codigoDepartamento
					}
				}
			}
		}
	`)

	const country = query.allContentfulTenaPaises.nodes.find(
		node => node.codigoDeDosLetras === locale.slice(-2)
	)

	if (!country) {
		console.error('No se encontró el país para el locale:', locale)
		return <p>No se encontraron datos para tu región.</p>
	}

	const [dataMaps, setDataMaps] = useState(country?.puntosDeVenta || [])
	const [defaulGeo, setDefaulGeo] = useState({
		center: {
			lat: country?.puntosDeVenta?.[0]?.lat || 0,
			lng: country?.puntosDeVenta?.[0]?.lng || 0,
		},
		zoom: 10,
	})
	const [userPos, setUserPos] = useState({
		lat: country?.puntosDeVenta?.[0]?.lat || 0,
		lng: country?.puntosDeVenta?.[0]?.lng || 0,
	})

	const [department, setDepartment] = useState('')
	const [city, setCity] = useState('')
	const [isChange, setIsChange] = useState(false)

	useEffect(() => {
		const fetchGeolocation = async () => {
			if (!navigator.geolocation) {
				console.error('Geolocalización no soportada por el navegador.')
				return
			}

			navigator.geolocation.getCurrentPosition(
				async position => {
					try {
						const { latitude, longitude } = position.coords
						const resp = await Axios.get(
							`https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${API_GOOGLE_KEY}`
						)

						const results = resp.data.results
						const locality = results.find(r => r.types.includes('locality'))
						const cityAux = locality?.address_components.find(component =>
							component.types.includes('locality')
						)

						if (country?.ciudades) {
							const city = country.ciudades.find(
								city => city.nombre === cityAux?.long_name?.toUpperCase()
							)
							if (city) {
								changeDepartment({ target: { value: city.codigoDepartamento } })
								setDefaulGeo({
									center: {
										lat: latitude,
										lng: longitude,
									},
									zoom: 16,
								})
							}
						}

						setUserPos({
							lat: latitude,
							lng: longitude,
						})
					} catch (error) {
						console.error('Error obteniendo geolocalización:', error)
					} finally {
						setIsChange(true)
					}
				},
				error => {
					console.error('Error de geolocalización:', error)
					setIsChange(true)
				}
			)
		}

		fetchGeolocation()
	}, [country])

	const changeDepartment = event => {
		setDepartment(event.target.value)
		setCity('')
		const dataFilter = country.puntosDeVenta.filter(info =>
			info.codigoDepartamento?.includes(event.target.value)
		)
		setDataMaps(dataFilter)
		setDefaulGeo({
			center: {
				lat: dataFilter[0]?.lat || 0,
				lng: dataFilter[0]?.lng || 0,
			},
			zoom: 14,
		})
	}

	const changeCity = event => {
		setCity(event.target.value)
		const dataAux = country.puntosDeVenta.filter(info =>
			info.codigoDepartamento?.includes(department)
		)
		const dataFilter = dataAux.filter(info => info.codigoCiudad?.includes(event.target.value))
		setDataMaps(dataFilter)
		setDefaulGeo({
			center: {
				lat: dataFilter[0]?.lat || 0,
				lng: dataFilter[0]?.lng || 0,
			},
			zoom: 17,
		})
	}

	const memoizedPoints = useMemo(
		() =>
			dataMaps.map(data => (
				<Point
					key={`${data.lat}-${data.lng}`}
					name={data.name}
					description={data.description}
					lat={data.lat}
					lng={data.lng}
				/>
			)),
		[dataMaps]
	)

	const memoizedUserPoint = useMemo(
		() => <Point user={true} name='Tu posición actual' lat={userPos.lat} lng={userPos.lng} />,
		[userPos]
	)

	return (
		<div className='f-map'>
			{isChange && (
				<>
					<div id='f-map-citys' className='f-map-citys'>
						<p className='f-map-citys-copy'>{country.nombreDepartamento}</p>
						<Select
							labelId='department'
							name='select-history'
							value={department}
							onChange={changeDepartment}
						>
							<MenuItem value=''>Selecciona tu {country.nombreDepartamento}</MenuItem>
							{(country.departamentos || []).map(dep => (
								<MenuItem key={dep.codigo} value={dep.codigo}>
									{dep.nombre}
								</MenuItem>
							))}
						</Select>
						{country.ciudades && (
							<>
								<p className='f-map-citys-copy'>{country.nombreCiudad}</p>
								<Select labelId='city' name='select-history' value={city} onChange={changeCity}>
									<MenuItem value=''>Selecciona tu {country.nombreCiudad}</MenuItem>
									{country.ciudades
										.filter(city => city.codigoDepartamento === department)
										.map(city => (
											<MenuItem key={city.codigo} value={city.codigo}>
												{city.nombre}
											</MenuItem>
										))}
								</Select>
							</>
						)}
					</div>
					<GoogleMapReact
						style={styleMap}
						bootstrapURLKeys={{ key: API_GOOGLE_KEY }}
						center={defaulGeo.center}
						zoom={defaulGeo.zoom}
						className='f-map-content'
					>
						{memoizedPoints}
						{memoizedUserPoint}
					</GoogleMapReact>
					<ListPoint userPos={userPos} dataMap={dataMaps} setDefaulGeo={setDefaulGeo} />
				</>
			)}
		</div>
	)
}

export default Map
